import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox } from '@/components/molecules/Combobox';
import {
    AUDIT_SCOPE_QUERY_MAX,
    AUDIT_SCOPE_QUERY_MIN,
    type AuditScopeOption,
    entityTypeText,
    scopeFilterLabel,
    useAuditScopeSearch,
    useAuditText,
} from '@/domain/auditLog';

import styles from './ScopeSearch.module.scss';

interface ScopeSearchProps {
    /** Names the search field. */
    label: string;
    onPick: (option: AuditScopeOption) => void;
    onClose: () => void;
}

const NO_OPTIONS: readonly AuditScopeOption[] = [];

function optionKey(option: AuditScopeOption): string {
    return `${option.kind}-${option.id}`;
}

/**
 * The open picker: a search over current client, site and pen names, archived and deleted
 * ones included, since the log still refers to them. It mounts only while the picker is
 * open, so a closed picker never searches.
 */
export default function ScopeSearch({ label, onPick, onClose }: ScopeSearchProps) {
    const { t } = useTranslation();
    const text = useAuditText();
    const [query, setQuery] = useState('');
    const search = useAuditScopeSearch(query);

    let status = '';
    if (!search.isSearchable) {
        status = t('auditLog.filters.scopeMinimum', { count: AUDIT_SCOPE_QUERY_MIN });
    } else if (search.isFetching) {
        status = t('auditLog.filters.scopeSearching');
    } else if (search.error) {
        status = t('auditLog.filters.scopeError');
    } else if (search.options.length === 0) {
        status = t('auditLog.filters.scopeNoMatches');
    }

    return (
        <Combobox
            variant="inline"
            autoFocus
            query={query}
            onQueryChange={setQuery}
            options={search.isSearchable ? search.options : NO_OPTIONS}
            getKey={optionKey}
            onSelect={onPick}
            onEscape={onClose}
            status={status}
            maxLength={AUDIT_SCOPE_QUERY_MAX}
            placeholder={t('auditLog.filters.wherePlaceholder')}
            aria-label={t('auditLog.filters.scopeSearchLabel', { label })}
            className={styles.body}
            renderOption={(option) => (
                <>
                    <span className={styles.kind}>{text(entityTypeText(option.kind))}</span>
                    <span className={styles.optionName}>
                        {text(
                            scopeFilterLabel(option.kind, option.id, {
                                entries: [],
                                scopes: [option],
                            })
                        )}
                    </span>
                </>
            )}
        />
    );
}
